import {useEffect, useState} from 'react';

import {type TypeaheadAddress} from '@/components/global/layout/AppBar/SearchBar/useTypeaheadData';
import {useGoogleMapsGeocode} from '@/hooks/useGoogleMapsGeocode';
import {useEventBarV2} from '../EventBarV2Provider';

type ResolvedOverride = {name: string; address: TypeaheadAddress | null};

/**
 * Resolves the event bar's chosen `Where` into a `TypeaheadAddress` for
 * `useTypeaheadData`'s `addressOverride`, so the `What` typeahead reflects a
 * location the user picked but has not committed to the fulfillment detail.
 *
 * - No selection → `null` (typeahead falls back to the committed fulfillment
 *   detail / fallback address).
 * - A geolocation selection already carries coordinates → mapped directly.
 * - An address selection (Google prediction / saved address) carries no
 *   coordinates → geocoded client-side via `fetchGeocodedAddress`.
 */
export const useEventBarTypeaheadAddress = (enabled: boolean): TypeaheadAddress | null => {
  const [resolved, setResolved] = useState<ResolvedOverride | null>(null);

  const {fetchGeocodedAddress} = useGoogleMapsGeocode();
  const {whereSelection} = useEventBarV2();

  const addressName = whereSelection?.kind === 'address' ? whereSelection.address.name : null;

  useEffect(() => {
    if (!addressName || !enabled) return undefined;

    let cancelled = false;

    fetchGeocodedAddress(addressName)
      .then(results => {
        if (cancelled) return;
        const first = results[0];
        setResolved({
          name: addressName,
          address: first
            ? {
                street: first.street,
                zip: first.zip,
                latitude: first.latitude,
                longitude: first.longitude,
              }
            : null,
        });
      })
      .catch(() => {
        if (!cancelled) setResolved({name: addressName, address: null});
      });

    return () => {
      cancelled = true;
    };
    // `fetchGeocodedAddress` gets a fresh identity each render; key the geocode
    // on the address string alone to avoid an infinite re-geocode loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressName, enabled]);

  if (!whereSelection) return null;

  if (whereSelection.kind === 'geolocation') {
    const {street, zip, latitude, longitude} = whereSelection.geocodedAddress;
    return {street, zip, latitude, longitude};
  }

  // Only surface a resolved override for the currently-selected address; while a
  // new selection is geocoding, `resolved` still holds the previous one.
  return resolved?.name === addressName ? resolved.address : null;
};

export default useEventBarTypeaheadAddress;
